import { useState, useRef, useCallback } from 'react';
import { Upload, X, GripVertical, Loader2 } from 'lucide-react';
import { adminApi } from '../api/admin';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
const MAX_IMAGES = 6;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

interface ImageItem {
  url: string;
  isNew?: boolean;
  file?: File;
}

interface ImageUploadProps {
  images: string[];
  productId?: number;
  onChange: (images: string[]) => void;
}

export default function ImageUpload({ images, productId, onChange }: ImageUploadProps) {
  const [items, setItems] = useState<ImageItem[]>(
    images.map(url => ({ url }))
  );
  const [uploading, setUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;
      if (file.size > MAX_FILE_SIZE) {
        alert(`Fichier ${file.name} trop volumineux (max 5MB)`);
        continue;
      }
      validFiles.push(file);
    }

    if (items.length + validFiles.length > MAX_IMAGES) {
      alert(`Maximum ${MAX_IMAGES} images autorisées`);
      return;
    }

    const newItems: ImageItem[] = validFiles.map(file => ({
      url: URL.createObjectURL(file),
      isNew: true,
      file,
    }));

    setItems(prev => [...prev, ...newItems]);
  }, [items.length]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleUpload = async () => {
    if (!productId) return;
    
    const newFiles = items.filter(item => item.isNew && item.file).map(item => item.file!);
    if (newFiles.length === 0) return;

    setUploading(true);
    try {
      const result = await adminApi.uploadProductImages(productId, newFiles);
      const uploadedUrls = result.all_images;
      onChange(uploadedUrls);
      setItems(uploadedUrls.map(url => ({ url })));
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Échec du téléchargement des images');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (index: number) => {
    const item = items[index];
    
    if (item.isNew) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      onChange(newItems.map(i => i.url));
    } else if (productId) {
      try {
        const result = await adminApi.deleteProductImage(productId, item.url);
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
        onChange(result.all_images);
      } catch (err) {
        console.error('Delete failed:', err);
      }
    } else {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      onChange(newItems.map(i => i.url));
    }
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    setItems(newItems);
    onChange(newItems.map(i => i.url));
  };

  const handleReorder = (fromIndex: number, toIndex: number) => {
    const newItems = [...items];
    const [removed] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, removed);
    setItems(newItems);
    onChange(newItems.map(i => i.url));
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Images ({items.length}/{MAX_IMAGES})
        </label>
        
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${items.length >= MAX_IMAGES 
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
              : 'border-gray-300 hover:border-lodiss-red hover:bg-red-50'}
          `}
        >
          <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">
            Glissez-déposez des images ici ou cliquez pour sélectionner
          </p>
          <p className="text-xs text-gray-400 mt-1">
            JPG, PNG, WebP - max 5MB par image
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            disabled={items.length >= MAX_IMAGES}
          />
        </div>
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {items.map((item, index) => (
            <div
              key={item.url + index}
              draggable
              onDragStart={() => setDraggedIndex(index)}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverIndex(index);
              }}
              onDragEnd={() => {
                if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
                  handleReorder(draggedIndex, dragOverIndex);
                }
                setDraggedIndex(null);
                setDragOverIndex(null);
              }}
              className={`
                relative group rounded-lg overflow-hidden border-2 transition-all
                ${dragOverIndex === index ? 'border-lodiss-red scale-105' : 'border-transparent'}
                ${index === 0 ? 'ring-2 ring-lodiss-red' : ''}
              `}
            >
              <img
                src={item.url.startsWith('http') ? item.url : `${API_BASE}${item.url}`}
                alt={`Image ${index + 1}`}
                className="w-full h-24 object-cover"
              />
              
              {index === 0 && (
                <span className="absolute top-1 left-1 bg-lodiss-red text-white text-xs px-1.5 py-0.5 rounded">
                  Principale
                </span>
              )}
              
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleMove(index, 'up'); }}
                  disabled={index === 0}
                  className="p-1 bg-white rounded disabled:opacity-50"
                >
                  <GripVertical size={12} className="rotate-90" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleMove(index, 'down'); }}
                  disabled={index === items.length - 1}
                  className="p-1 bg-white rounded disabled:opacity-50"
                >
                  <GripVertical size={12} className="rotate-90" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDelete(index); }}
                  className="p-1 bg-red-500 text-white rounded"
                >
                  <X size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {productId && items.some(item => item.isNew) && (
        <button
          type="button"
          onClick={handleUpload}
          disabled={uploading}
          className="w-full py-2 bg-lodiss-red text-white rounded-lg hover:bg-lodiss-dark disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {uploading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Téléchargement...
            </>
          ) : (
            'Enregistrer les images'
          )}
        </button>
      )}
    </div>
  );
}
